import type { Ticket } from "@kiraah-shared/common";
import { LvfTicketArticleNature } from "@kiraah-shared/common";

/**
 * Méta-d’étape « shell » : aucun métier ticket — réutilisable par d’autres wizards mobile (stock, etc.).
 */
export interface MobileWizardStepMeta {
  readonly key: string;
  readonly labelKey: string;
}

/** @deprecated Préférer {@link MobileWizardStepMeta}. */
export type MobileDemandWizardStepMeta = MobileWizardStepMeta;

/**
 * Construction de la séquence d’étapes alignée sur `TicketWizardComponent.buildTicketTabs` (daily-activity) :
 *
 * | Web `ModuleTabItem.Key` ({@link TicketWizardComponent}) | Composant web (standalone)                         | Étape mobile `key` | Rôle |
 * |---|---|:---:|---|
 * | `generalinfo` | {@link TicketGeneralInfoComponent} | `general` | Infos + demandeur / destinataire |
 * | `productselection` | {@link TicketProductSelectionComponent} | `products` | Catalogue + lignes (PERISHABLE / SERVICE) |
 * | `equipmenttypeselection` | {@link TicketEquipmentTypeSelectionComponent} | `equipment-types` | Types d’équipement (VEHICLE_TYPE) |
 * | `equipmentselection` | {@link TicketEquipmentSelectionComponent} | `equipment-selection` | Équipements (VEHICLE) |
 * | `deliveryaddress` | {@link TicketDeliveryAddressComponent} | `address` | Modes livraison + agence / adresse manuelle (`ticket-delivery-address/`) |
 * | `summary` | {@link TicketCreationSummaryComponent} | `summary` | Récap avant envoi (`ticket-summary/`) |
 *
 * Données côté web : `WizardInstanceId` + {@link WizardDataService} dans chaque widget. Mobile : équivalent fonctionnel via l’orchestrateur (`MobileTicketWizardComponent`) jusqu’à extraction de sous-composants miroir (`ticket-general-info/`, `ticket-product-selection/`, `ticket-equipment-selection/`, `ticket-delivery-address/`, `ticket-summary/`).
 *
 * Chemins fichiers web : `ticket-creation-wizard/ticket-wizard.component.ts` et dossiers homonymes sous le même dossier.
 */
export function buildTicketDemandWizardSteps(ticket: Ticket | null): readonly MobileWizardStepMeta[] {
  const general: MobileWizardStepMeta = {
    key: "general",
    labelKey: "mobile.eachat.wizard.general",
  };
  const address: MobileWizardStepMeta = {
    key: "address",
    labelKey: "mobile.eachat.wizard.address",
  };
  const summary: MobileWizardStepMeta = {
    key: "summary",
    /** Court pour la barre d’étapes (évite la troncature) ; titre complet dans l’écran récap {@link MobileTicketSummaryStepComponent}. */
    labelKey: "mobile.eachat.wizard.summaryTitle",
  };

  if (!ticket?.TicketType?.LvfTicketArticleNature) {
    return [general, address, summary];
  }

  const nature = ticket.TicketType.LvfTicketArticleNature;

  if (
    nature === LvfTicketArticleNature.PERISHABLE_PRODUCT ||
    nature === LvfTicketArticleNature.SERVICE
  ) {
    return [
      general,
      { key: "products", labelKey: "mobile.eachat.wizard.products" },
      address,
      summary,
    ];
  }

  if (nature === LvfTicketArticleNature.VEHICLE || nature === LvfTicketArticleNature.VEHICLE_TYPE) {
    if (nature === LvfTicketArticleNature.VEHICLE_TYPE) {
      return [
        general,
        { key: "equipment-types", labelKey: "mobile.eachat.wizard.vehicleTypesStep" },
        address,
        summary,
      ];
    }
    return [
      general,
      { key: "equipment-selection", labelKey: "mobile.eachat.wizard.vehicleSelectionStep" },
      address,
      summary,
    ];
  }

  return [general, address, summary];
}

/** Clé d’étape web {@link TicketWizardSliceCoordinator.saveDepartingStep$} depuis l’étape mobile. */
export function mapMobileTicketWizardStepToWebKey(mobileKey: string | undefined): string {
  switch (mobileKey) {
    case "general":
      return "generalinfo";
    case "products":
      return "productselection";
    case "equipment-types":
      return "equipmenttypeselection";
    case "equipment-selection":
      return "vehicleselection";
    case "address":
      return "deliveryaddress";
    case "summary":
      return "deliveryaddress";
    default:
      return "generalinfo";
  }
}
